import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../utils/config';
import ProfileModal from './ProfileModal';
import RoleSwitchModal from './RoleSwitchModal';
import DepartmentSwitchModal from './DepartmentSwitchModal';
import './GlobalHeader.css';

const GlobalHeader = () => {
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isRoleSwitchOpen, setIsRoleSwitchOpen] = useState(false);
  const [isDepartmentSwitchOpen, setIsDepartmentSwitchOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(sessionStorage.getItem("image_url"));
  const profileContainerRef = useRef(null);

  useEffect(() => {
    const refreshProfileImage = () => {
      setProfileImage(sessionStorage.getItem("image_url"));
    };
    window.addEventListener("profile-updated", refreshProfileImage);
    return () => window.removeEventListener("profile-updated", refreshProfileImage);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileContainerRef.current && !profileContainerRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = config.logoutUrl;
  };

  const hasMultipleRoles = () => {
    try {
      const rolesList = JSON.parse(sessionStorage.getItem('user_roles') || '[]');
      return Array.isArray(rolesList) && rolesList.length > 1;
    } catch (e) {
      return false;
    }
  };

  return (
    <>
      <header className="new-fixed-header">
        <div className="new-header-content">
          <div className="new-header-left">
            <img 
              src="/crlogo.png" 
              alt="CR Logo" 
              className="header-logo"
              onClick={() => navigate('/Home')}
            />
            <span className="capability-index-text">Capability Index</span>
          </div>

          <div className="new-header-right" ref={profileContainerRef}>
            <div className="new-divider"></div>

            <div className="new-profile-btn" onClick={() => setShowProfileDropdown(!showProfileDropdown)}>
              <div className="new-profile-avatar">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="new-profile-avatar-img" />
                ) : (
                  (sessionStorage.getItem('user_name') || 'User')
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                )}
              </div>

              <div className="new-profile-info">
                <div className="new-profile-name">{sessionStorage.getItem('user_name')}</div>
                <div className="new-profile-role">
                  {sessionStorage.getItem('access_role') === 'admin' ? 'Administrator' : 
                   sessionStorage.getItem('access_role') === 'manager' ? 'Manager' : 'User'}
                </div>
              </div>
              <svg className="new-dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>

            {showProfileDropdown && (
              <div className="profile-dropdown-menu">
                <div className="dropdown-user-header">
                  <div className="dropdown-large-avatar">
                    {profileImage ? (
                      <img src={profileImage} alt="Profile" className="dropdown-avatar-img" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      sessionStorage.getItem('user_name')?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) || 'JD'
                    )}
                  </div>
                  <div className="dropdown-user-info">
                    <div className="dropdown-user-name">{sessionStorage.getItem('user_name')}</div>
                    <div className="dropdown-user-email">{sessionStorage.getItem('user_emailId')}</div>
                    <span className="dropdown-role-badge">
                      {sessionStorage.getItem('access_role') === 'admin' ? 'Administrator' : 
                       sessionStorage.getItem('access_role') === 'manager' ? 'Manager' : 'User'}
                    </span>
                  </div>
                </div>

                <div className="dropdown-menu-list">
                  <div className="dropdown-menu-item" onClick={() => { setShowProfileDropdown(false); setShowProfileModal(true); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="7" r="4"/>
                      <path d="M20 21v-2a8 8 0 0 0-16 0v2"/>
                    </svg>
                    <span>My Profile</span>
                  </div>

                  {['admin', 'manager'].includes(sessionStorage.getItem('access_role')?.toLowerCase()) && (
                    <div className="dropdown-menu-item" onClick={() => { setShowProfileDropdown(false); navigate('/roles'); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="8.5" cy="7" r="4"/>
                        <path d="M20 8v5"/>
                        <path d="M23 11l-3-3-3 3"/>
                      </svg>
                      <span>Roles Management</span>
                    </div>
                  )}
                  
                  {['admin', 'manager'].includes(sessionStorage.getItem('access_role')?.toLowerCase()) && (
                    <div className="dropdown-menu-item" onClick={() => { setShowProfileDropdown(false); navigate('/department-management'); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                      </svg>
                      <span>Manage Departments</span>
                    </div>
                  )}
                  
                  {hasMultipleRoles() && (
                    <div className="dropdown-menu-item" onClick={() => { setShowProfileDropdown(false); setIsRoleSwitchOpen(true); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 9l-7 7-7-7"/>
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0z"/>
                      </svg>
                      <span>Role Switch</span>
                    </div>
                  )}

                  <div className="dropdown-menu-item" onClick={() => { setShowProfileDropdown(false); setIsDepartmentSwitchOpen(true); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                    <span>Department Switch</span>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-menu-item logout-item" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 16l4-4m0 0l-4-4m4 4H7"/>
                      <path d="M13 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h7"/>
                    </svg>
                    <span>Logout</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
      <RoleSwitchModal isOpen={isRoleSwitchOpen} onClose={() => setIsRoleSwitchOpen(false)} currentRole={sessionStorage.getItem('access_role')} onRoleSwitch={(newRole) => { sessionStorage.setItem('access_role', newRole); window.location.reload(); }} />
      <DepartmentSwitchModal isOpen={isDepartmentSwitchOpen} onClose={() => setIsDepartmentSwitchOpen(false)} currentDepartment={sessionStorage.getItem('user_department')} onDepartmentSwitch={(newDept) => { sessionStorage.setItem('user_department', newDept); window.location.reload(); }} availableDepartments={[]} />
    </>
  );
};

export default GlobalHeader;
